import React, { useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Slide,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  OpenInFull as ExpandIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  DeleteSweep as ClearIcon,
} from '@mui/icons-material';
import { useActivityLog } from '../context/ActivityLogContext';

const ActivityLogPanel = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const {
    processes,
    panelOpen,
    setPanelOpen,
    panelMinimized,
    setPanelMinimized,
    activeProcessId,
    setActiveProcessId,
    removeActivityProcess,
    clearFinishedProcesses,
  } = useActivityLog();

  const logsEndRef = useRef(null);

  // Theme colors
  const primaryColor = isDarkMode ? '#bbb0ff' : '#1b2a41';
  const primaryColorLight = isDarkMode ? 'rgba(187, 176, 255, 0.15)' : 'rgba(27, 42, 65, 0.1)';

  const processIds = Object.keys(processes);
  const activeProcess = activeProcessId ? processes[activeProcessId] : null;
  const hasFinishedProcesses = processIds.some(
    id => processes[id].status === 'completed' || processes[id].status === 'error'
  );

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (logsEndRef.current && !panelMinimized) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeProcess?.logs, panelMinimized]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'error': return isDarkMode ? '#ff6b6b' : '#f44336';
      case 'processing': return primaryColor;
      default: return isDarkMode ? '#a3a3a3' : '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'error': return <ErrorIcon sx={{ fontSize: 16 }} />;
      default: return <PendingIcon sx={{ fontSize: 16 }} />;
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return '';
    const end = endTime ? new Date(endTime) : new Date();
    const start = new Date(startTime);
    const diff = Math.floor((end - start) / 1000);
    if (diff < 60) return `${diff}s`;
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${mins}m ${secs}s`;
  };

  if (!panelOpen || processIds.length === 0) {
    return null;
  }

  return (
    <Slide direction="up" in={panelOpen} mountOnEnter unmountOnExit>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          left: 280,
          bottom: 20,
          width: panelMinimized ? 320 : 420,
          maxHeight: panelMinimized ? 'auto' : '60vh',
          zIndex: 1250,
          overflow: 'hidden',
          borderRadius: 3,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isDarkMode ? '#1c1d1f' : '#ffffff',
          border: `1px solid ${isDarkMode ? 'rgba(187, 176, 255, 0.3)' : 'rgba(27, 42, 65, 0.2)'}`,
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(187, 176, 255, 0.1)'
            : '0 8px 32px rgba(27, 42, 65, 0.15), 0 0 0 1px rgba(27, 42, 65, 0.05)',
          animation: 'slideUp 0.3s ease-out',
          '@keyframes slideUp': {
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: activeProcess?.status === 'completed'
              ? 'linear-gradient(135deg, #4caf50, #2e7d32)'
              : activeProcess?.status === 'error'
              ? isDarkMode
                ? 'linear-gradient(135deg, #ff6b6b, #c62828)'
                : 'linear-gradient(135deg, #f44336, #c62828)'
              : isDarkMode
              ? 'linear-gradient(135deg, #bbb0ff, #9575cd)'
              : 'linear-gradient(135deg, #1b2a41, #324a69)',
            color: isDarkMode && activeProcess?.status === 'processing' ? '#131415' : 'white',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {activeProcess && getStatusIcon(activeProcess.status)}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {activeProcess?.interfaceName || 'Activity Log'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                {activeProcess?.status === 'processing' ? 'Processing...' : activeProcess?.status === 'completed' ? 'Completed' : activeProcess?.status === 'error' ? 'Error occurred' : 'Pending'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasFinishedProcesses && (
              <Tooltip title="Clear finished">
                <IconButton
                  size="small"
                  sx={{
                    color: isDarkMode && activeProcess?.status === 'processing' ? '#131415' : 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                  }}
                  onClick={clearFinishedProcesses}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              sx={{
                color: isDarkMode && activeProcess?.status === 'processing' ? '#131415' : 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
              }}
              onClick={() => setPanelMinimized(!panelMinimized)}
            >
              {panelMinimized ? <ExpandIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              sx={{
                color: isDarkMode && activeProcess?.status === 'processing' ? '#131415' : 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
              }}
              onClick={() => setPanelOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Process Tabs */}
        {!panelMinimized && processIds.length > 1 && (
          <Tabs
            value={activeProcessId || processIds[0]}
            onChange={(e, newValue) => setActiveProcessId(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: isDarkMode ? 'rgba(187, 176, 255, 0.2)' : 'rgba(27, 42, 65, 0.15)',
              bgcolor: isDarkMode ? 'rgba(187, 176, 255, 0.05)' : 'rgba(27, 42, 65, 0.03)',
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, py: 0.5 },
              '& .MuiTabs-indicator': {
                bgcolor: primaryColor,
              },
            }}
          >
            {processIds.map(id => (
              <Tab
                key={id}
                value={id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(processes[id].status),
                      }}
                    />
                    <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                      {processes[id].interfaceName || processes[id].name}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ ml: 0.5, p: 0.25, '&:hover': { bgcolor: primaryColorLight } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeActivityProcess(id);
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                }
              />
            ))}
          </Tabs>
        )}

        {/* Minimized View */}
        {panelMinimized && activeProcess && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 180, fontWeight: 500 }}>
                {activeProcess.interfaceName || activeProcess.name}
              </Typography>
              <Chip
                size="small"
                label={`${Math.round(activeProcess.progressPercentage)}%`}
                sx={{
                  backgroundColor: getStatusColor(activeProcess.status),
                  color: activeProcess.status === 'processing' && isDarkMode ? '#131415' : 'white',
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={activeProcess.progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: isDarkMode ? 'rgba(187, 176, 255, 0.15)' : 'rgba(27, 42, 65, 0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getStatusColor(activeProcess.status),
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        )}

        {/* Expanded Content */}
        {!panelMinimized && activeProcess && (
          <>
            {/* Progress Section */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: isDarkMode ? 'rgba(187, 176, 255, 0.15)' : 'rgba(27, 42, 65, 0.1)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {activeProcess.name}
                </Typography>
                <Chip
                  size="small"
                  label={formatDuration(activeProcess.startTime, activeProcess.endTime)}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: primaryColorLight,
                    color: primaryColor,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={activeProcess.progressPercentage}
                  sx={{
                    flex: 1,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: isDarkMode ? 'rgba(187, 176, 255, 0.15)' : 'rgba(27, 42, 65, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getStatusColor(activeProcess.status),
                      borderRadius: 5,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'right', fontWeight: 600, color: primaryColor }}>
                  {Math.round(activeProcess.progressPercentage)}%
                </Typography>
              </Box>
              {activeProcess.totalItems > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {activeProcess.processedItems} / {activeProcess.totalItems} items processed
                </Typography>
              )}
            </Box>

            {/* Logs Section */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 1.5,
                backgroundColor: isDarkMode ? '#131415' : '#f5f5f5',
                maxHeight: 280,
                minHeight: 120,
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: isDarkMode ? 'rgba(187, 176, 255, 0.3)' : 'rgba(27, 42, 65, 0.2)',
                  borderRadius: 3,
                },
              }}
            >
              {activeProcess.logs.length === 0 ? (
                <Typography variant="body2" sx={{ color: isDarkMode ? '#888' : '#666', fontStyle: 'italic' }}>
                  Waiting for logs...
                </Typography>
              ) : (
                activeProcess.logs.map((log, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: log.toLowerCase().includes('error') || log.toLowerCase().includes('failed')
                        ? '#ff6b6b'
                        : log.toLowerCase().includes('success') || log.toLowerCase().includes('completed')
                        ? '#4caf50'
                        : log.toLowerCase().includes('warning')
                        ? '#ffa726'
                        : isDarkMode ? '#e0e0e0' : '#424242',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      mb: 0.5,
                      lineHeight: 1.4,
                    }}
                  >
                    {log}
                  </Typography>
                ))
              )}
              <div ref={logsEndRef} />
            </Box>
          </>
        )}
      </Paper>
    </Slide>
  );
};

export default ActivityLogPanel;
